import React, { useState } from 'react';
import { X, Key, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { useLicense } from '../contexts/LicenseContext';

const LicenseModal = ({ isOpen, onClose, canClose = true }) => {
  const { 
    activateLicense, 
    isTrialMode, 
    trialDaysRemaining, 
    needsActivation 
  } = useLicense();
  
  const [licenseKey, setLicenseKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!isOpen) return null;

  const handleActivate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!licenseKey.trim()) {
      setError('Please enter a license key');
      return;
    }

    // Basic format validation - accept both MAIDLY and UUID formats
    const maidlyPattern = /^MAIDLY-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
    const uuidPattern = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
    
    if (!maidlyPattern.test(licenseKey) && !uuidPattern.test(licenseKey)) {
      setError('Invalid license key format. Expected: MAIDLY-XXXX-XXXX-XXXX or UUID format');
      return;
    }

    setLoading(true);
    const result = await activateLicense(licenseKey);
    setLoading(false);

    if (result.success) {
      setSuccess(result.message);
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
    } else {
      setError(result.message);
    }
  };

  const handleKeyPress = (e) => {
    // Allow only alphanumeric and dash
    const char = e.key;
    if (!/[A-Za-z0-9-]/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight') {
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/[^A-Za-z0-9-]/g, '');
    // Convert to uppercase for MAIDLY format, preserve case for UUID format
    const processedPaste = paste.startsWith('MAIDLY') ? paste.toUpperCase() : paste;
    setLicenseKey(processedPaste);
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        maxWidth: '500px',
        width: '100%',
        padding: '2rem',
        position: 'relative',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Close button - only show if closeable */}
        {canClose && !needsActivation && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              color: '#6b7280'
            }}
          >
            <X size={24} />
          </button>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 1rem',
            background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Key size={32} color="#fff" />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1a1a' }}>
            {needsActivation ? 'Trial Expired - Activate License' : 'Activate Your License'}
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            {needsActivation 
              ? 'Your 15-day trial has ended. Please enter a valid license key to continue.' 
              : 'Enter your license key to unlock full access'}
          </p>
        </div>

        {/* Trial Status */}
        {isTrialMode && !needsActivation && (
          <div style={{
            padding: '1rem',
            background: trialDaysRemaining <= 3 ? '#fff4de' : '#e0f2ff',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'start'
          }}>
            <Clock size={20} color={trialDaysRemaining <= 3 ? '#946800' : '#0073ea'} style={{ flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, color: trialDaysRemaining <= 3 ? '#946800' : '#0073ea', marginBottom: '0.25rem' }}>
                Trial Period: {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Activate your license now to ensure uninterrupted access
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleActivate}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#1a1a1a' }}>
              License Key
            </label>
            <input
              type="text"
              value={licenseKey}
              onChange={(e) => {
                const value = e.target.value;
                // Convert to uppercase for MAIDLY format, preserve case for UUID format
                const processedValue = value.startsWith('MAIDLY') ? value.toUpperCase() : value;
                setLicenseKey(processedValue);
              }}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              placeholder="MAIDLY-XXXX-XXXX-XXXX or UUID"
              maxLength={36}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'monospace',
                letterSpacing: '1px',
                textAlign: 'center',
                textTransform: 'none'
              }}
              disabled={loading}
              autoFocus
            />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
              Enter your license key (MAIDLY format or UUID) received after purchase
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '0.75rem',
              background: '#ffdbde',
              borderRadius: '6px',
              marginBottom: '1rem',
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center'
            }}>
              <AlertCircle size={18} color="#c1003d" />
              <span style={{ color: '#c1003d', fontSize: '0.875rem' }}>{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div style={{
              padding: '0.75rem',
              background: '#d3f5e9',
              borderRadius: '6px',
              marginBottom: '1rem',
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'center'
            }}>
              <CheckCircle size={18} color="#00854d" />
              <span style={{ color: '#00854d', fontSize: '0.875rem' }}>{success}</span>
            </div>
          )}

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              type="submit"
              disabled={loading || !licenseKey}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: loading || !licenseKey ? '#e5e7eb' : 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '1rem',
                cursor: loading || !licenseKey ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.2s'
              }}
            >
              {loading ? 'Activating...' : 'Activate License'}
            </button>
            {canClose && !needsActivation && (
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f5f6f8',
                  color: '#323338',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default LicenseModal;
